<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-container fluid py-0 pb-1>
          <v-row align="center">
            <div>
              <h1 class="headline">Open Groups</h1>
            </div>
            <v-col class="py-0">
              <list-filters
                :filters="filters"
                label="Find Open Group"
                ref-name="openGroupsListFilters"
                @searchParamsChanged="searchParamsChanged($event)"
                :expansion-panel="false"
              ></list-filters>
            </v-col>
            <div>
              <list-visible-columns-and-filter
                :visible-columns="visibleColumns"
                :selectable-headers="selectableHeaders"
                v-model="search"
                @change="onVisibleColumnsChange"
              ></list-visible-columns-and-filter>
            </div>
            <div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    fab
                    dark
                    color="primary"
                    @click="onCreateNew"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Create New Open Group</span>
              </v-tooltip>
            </div>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-data-table
      :headers="filteredHeaders"
      :items="items"
      :loading="$apollo.loading"
      v-bind="pagingOptions"
      :search="search"
      item-key="id"
      class="elevation-1"
      fixed-header
      hide-default-footer
      @update:options="onListOptionsUpdate"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
    >
      <template v-slot:item.destination="{ item }">
        {{ item.destination && item.destination.name }}
      </template>
      <template v-slot:item.validFrom="{ item }">
        {{ item.validFrom | formatDate }}
      </template>
      <template v-slot:item.validTo="{ item }">
        {{ item.validTo | formatDate }}
      </template>
      <template v-slot:item.actions="{ item }">
        <default-list-actions
          :item="item"
          :hide-view="true"
          @deleteItem="deleteItem($event)"
          @editItem="editItem"
        ></default-list-actions>
      </template>
    </v-data-table>

    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="openGroups"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('openGroups')"
    ></get-more-results>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { ListMixin } from "../../../mixins/ListMixin";
import { removeGQLTypename } from "../../../helpers";
import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import ListFilters from "../shared/list-filters/ListFilters";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import openGroupFilters from "./open-group-filters";

const OPEN_GROUPS_QUERY = gql`
  query ($after: String, $first: Int, $filter: OpenGroupFilterType) {
    openGroups(after: $after, first: $first, filter: $filter) {
      totalCount
      edges {
        node {
          id
          name
          description
          department
          duration
          adults
          children
          validFrom
          validTo
          departureDays
          destination {
            id
            name
          }
        }
      }
    }
  }
`;

const DELETE_OPEN_GROUP = gql`
  mutation ($id: ID!) {
    deleteOpenGroup(id: $id) {
      id
    }
  }
`;

export default {
  name: "OpenGroupList",

  props: {
    refName: {
      type: String,
      default: () => "open-groups-list",
    },
  },

  mixins: [ListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
  },

  data() {
    return {
      openGroups: {},
      search: "",
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "destination",
        "department",
        "duration",
        "validFrom",
        "validTo",
        "actions",
      ]),
      headers: [
        { text: "Name", value: "name", name: "name" },
        { text: "Destination", value: "destination", name: "destination" },
        { text: "Department", value: "department", name: "department" },
        { text: "Duration", value: "duration", name: "duration" },
        { text: "Adults", value: "adults", name: "adults" },
        { text: "Children", value: "children", name: "children" },
        { text: "Valid From", value: "validFrom", name: "validFrom" },
        { text: "Valid To", value: "validTo", name: "validTo" },
        { text: "Departure Days", value: "departureDays", name: "departureDays" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    items() {
      return this.openGroups && this.openGroups.edges
        ? this.openGroups.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return openGroupFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    editItem(item) {
      this.$router.push(`/open-group/${item.id}/edit`);
    },
    onCreateNew() {
      this.$router.push("/open-group/new");
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DELETE_OPEN_GROUP,
          variables: { id: item.id },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", { msg: "Open Group removed." });
          this.$apollo.queries.openGroups.refetch();
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", { msg: errorMsg, color: "error" });
        });
    },
  },

  apollo: {
    openGroups: {
      query: OPEN_GROUPS_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: this.searchParams,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.openGroups.totalCount;
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/OpenGroupList.vue